import React, { useState } from "react";
import { deplacements, contact, infosPratiques } from "../mock";
import {
  Car,
  MapPin,
  ArrowRight,
  CreditCard,
  Percent,
  CalendarX,
  Ban,
  Clock,
  Timer,
  UserX,
  ShieldAlert
} from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";

const infoIcons = [CreditCard, Percent, CalendarX, Ban, Timer, Clock, UserX, ShieldAlert];

const PracticalInfo = () => {
  const [open, setOpen] = useState(false);

  return (
    <section id="infos" className="py-24 md:py-28 bg-sand">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="text-center mb-14">
          <p className="font-serif text-[11px] tracking-[0.35em] text-gold-dark mb-6">INFOS PRATIQUES</p>
          <h2 className="font-serif text-4xl md:text-5xl text-brown">
            À <span className="font-vibes text-gold-dark text-5xl md:text-6xl">savoir</span> avant votre rendez-vous
          </h2>
          <div className="gold-divider my-6">
            <span className="w-1.5 h-1.5 rounded-full bg-gold" />
          </div>
          <p className="font-cormorant italic text-brown/75 text-lg max-w-2xl mx-auto">
            Pour que votre expérience chez Maison Morgane se déroule dans les meilleures conditions.
          </p>
        </div>

        {/* Infos pratiques grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {infosPratiques.map((info, i) => {
            const Icon = infoIcons[i] || Clock;
            return (
              <div key={i} className="bg-cream border border-taupe/40 p-6">
                <div className="w-10 h-10 rounded-full bg-sand border border-gold/30 flex items-center justify-center mb-4">
                  <Icon size={16} className="text-gold-dark" strokeWidth={1.3} />
                </div>
                <p className="font-serif text-[11px] tracking-[0.25em] text-brown uppercase mb-2">{info.title}</p>
                <p className="font-cormorant text-brown/80 text-[15px] leading-relaxed">{info.desc}</p>
              </div>
            );
          })}
        </div>

        {/* Déplacements */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-cream border border-taupe/40 p-8 md:p-12">
            <div className="flex items-center gap-3 mb-5">
              <Car size={22} strokeWidth={1.3} className="text-gold-dark" />
              <h3 className="font-serif text-2xl md:text-3xl text-brown">{deplacements.title}</h3>
            </div>
            <p className="font-cormorant text-brown/85 text-lg leading-relaxed mb-4">
              {deplacements.desc}
            </p>
            <p className="font-cormorant text-brown text-lg leading-relaxed mb-4">
              <span className="font-semibold">{deplacements.descBis}</span>
            </p>
            <p className="font-cormorant italic text-brown/70 text-[15px] leading-relaxed">
              {deplacements.info}
            </p>
            <div className="mt-6 pt-5 border-t border-taupe/40 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3 text-brown/70">
                <MapPin size={16} strokeWidth={1.3} className="text-gold-dark" />
                <a
                  href={contact.mapsUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="font-cormorant text-base link-underline"
                >
                  {contact.fullAddress}
                </a>
              </div>
              <a
                href={contact.whatsappUrl}
                target="_blank"
                rel="noreferrer"
                className="btn-outline-gold !py-2.5"
              >
                Demander un devis <ArrowRight size={14} strokeWidth={1.4} />
              </a>
            </div>
          </div>
        </div>

        {/* Legal mentions trigger */}
        <div className="text-center mt-12">
          <button
            onClick={() => setOpen(true)}
            className="font-serif text-[10px] tracking-[0.3em] uppercase text-brown/60 hover:text-gold-dark link-underline"
          >
            Mentions légales & confidentialité
          </button>
        </div>
      </div>

      {/* Legal Modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl bg-cream border-taupe/40 max-h-[85vh] overflow-y-auto">
          <DialogTitle className="font-serif text-2xl md:text-3xl text-brown text-center">
            Mentions <span className="font-vibes text-gold-dark text-3xl md:text-4xl">légales</span>
          </DialogTitle>
          <DialogDescription className="font-cormorant italic text-brown/70 text-center">
            Informations légales et politique de confidentialité
          </DialogDescription>

          <div className="gold-divider my-2">
            <span className="w-1 h-1 rounded-full bg-gold" />
          </div>

          <div className="space-y-5 font-cormorant text-brown/85 text-[15px] leading-relaxed">
            <div>
              <p className="font-serif text-[11px] tracking-[0.25em] uppercase text-gold-dark mb-2">Éditeur du site</p>
              <p>
                <span className="font-semibold">Maison Morgane</span> — Institut privé de beauté & bien-être<br />
                {contact.fullAddress}<br />
                Email : <a href={`mailto:${contact.email}`} className="underline">{contact.email}</a><br />
                Téléphone / WhatsApp : {contact.whatsapp}
              </p>
            </div>

            <div>
              <p className="font-serif text-[11px] tracking-[0.25em] uppercase text-gold-dark mb-2">Hébergement</p>
              <p>Site hébergé sur infrastructure sécurisée.</p>
            </div>

            <div>
              <p className="font-serif text-[11px] tracking-[0.25em] uppercase text-gold-dark mb-2">Propriété intellectuelle</p>
              <p>L'ensemble des contenus présents sur ce site (textes, images, logos, graphismes) est la propriété exclusive de Maison Morgane. Toute reproduction, même partielle, est interdite sans autorisation écrite préalable.</p>
            </div>

            <div>
              <p className="font-serif text-[11px] tracking-[0.25em] uppercase text-gold-dark mb-2">Données personnelles (RGPD)</p>
              <p>Aucune donnée personnelle n'est collectée via ce site. Les réservations se font exclusivement via Instagram ou WhatsApp, où les échanges sont soumis aux politiques respectives de ces plateformes.</p>
              <p className="mt-2">Conformément au RGPD, vous disposez d'un droit d'accès, de rectification et de suppression des données vous concernant. Contact : <a href={`mailto:${contact.email}`} className="underline">{contact.email}</a>.</p>
            </div>

            <div>
              <p className="font-serif text-[11px] tracking-[0.25em] uppercase text-gold-dark mb-2">Cookies</p>
              <p>Ce site n'utilise pas de cookies de traçage publicitaire.</p>
            </div>
          </div>

          <button
            onClick={() => setOpen(false)}
            className="btn-outline-gold mt-4 mx-auto"
          >
            Fermer
          </button>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default PracticalInfo;
